'use client';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import {DEFAULT_HERO_MODE, DEFAULT_HERO_SIZE, HeroMode, heroFlowHeight, type HeroSize} from '../hero';
import {Section} from '../primitives';
import {CardSkeleton} from './CardSkeleton';
import {MastheadSkeleton} from './MastheadSkeleton';

// Mirrors the homepage's above-the-fold rhythm: a full-bleed Hero (matching
// HeroSection's 550/881 heights), then the Spotlight section (two-column info +
// 3-tile collage), then the FeaturedProjects carousel. The real page renders
// more sections below these, but they're off the first paint.

// The tall right-hand collage tile spans both rows.
const COLLAGE_SPAN = '1 / span 2';

// SpotlightSection: a two-column row (info at the start, 3-tile image collage at
// the end) that stacks on mobile with the collage on top (order -1).
function SpotlightSkeleton() {
  const tile = (extra: object) => (
    <Skeleton
      variant="rounded"
      animation="wave"
      sx={theme => ({minHeight: 200, height: '100%', borderRadius: `${theme.radii.images}px`, ...extra})}
    />
  );
  return (
    <Section tone="raised">
      <Container>
        <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, alignItems: {md: 'center'}, gap: {xs: 5, md: 22}}}>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.25, flex: 1, minWidth: 0}}>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1.75}}>
                <Skeleton variant="circular" animation="wave" width={10} height={10} sx={{flexShrink: 0}} />
                <Skeleton variant="text" animation="wave" width={110} sx={{fontSize: theme => theme.font.size.label}} />
              </Box>
              <Skeleton variant="text" animation="wave" width="65%" sx={{fontSize: theme => theme.font.size['title-2']}} />
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                <Skeleton variant="text" animation="wave" width="100%" />
                <Skeleton variant="text" animation="wave" width="88%" />
              </Box>
            </Box>
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, minmax(0, 200px))'}, gap: 1}}>
              {[0, 1].map(i => (
                <Box
                  key={i}
                  sx={theme => ({
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 3,
                    borderRadius: `${theme.radii.xl}px`,
                    background: theme.palette.colors.surface.neutral.subtle,
                  })}
                >
                  <Skeleton variant="text" animation="wave" width="70%" sx={{fontSize: theme => theme.font.size['body-2']}} />
                  <Skeleton variant="text" animation="wave" width="50%" sx={{fontSize: theme => theme.font.size['body-1']}} />
                </Box>
              ))}
            </Box>
            <Skeleton
              variant="rounded"
              animation="wave"
              height={48}
              sx={theme => ({width: {xs: '100%', md: 160}, mt: 0.75, borderRadius: `${theme.radii.button}px`})}
            />
          </Box>
          <Box
            sx={{
              order: {xs: -1, md: 0},
              flex: 1,
              minWidth: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gridAutoRows: '1fr',
              gap: 2,
              minHeight: {md: 476},
            }}
          >
            {tile({gridColumn: 1, gridRow: 1, mt: {md: 3}})}
            {tile({gridColumn: 1, gridRow: 2})}
            {tile({gridColumn: 2, gridRow: COLLAGE_SPAN, mb: {md: 3}})}
          </Box>
        </Box>
      </Container>
    </Section>
  );
}

// CarouselSection (FeaturedProjects): neutral background, a title + subtitle
// heading, a horizontal row of ~3 cards, and a centered view-all button below.
export function CarouselSectionSkeleton() {
  return (
    <Section tone="subtle">
      <Container sx={{mb: 3.25}}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
          <Skeleton variant="text" animation="wave" width={240} sx={{fontSize: theme => theme.font.size['title-4']}} />
          <Skeleton variant="text" animation="wave" width={320} sx={{fontSize: theme => theme.font.size['body-2']}} />
        </Box>
      </Container>
      <Container>
        <Box sx={{display: 'flex', gap: {xs: 1.5, md: 4}, overflow: 'hidden'}}>
          {Array.from({length: 3}, (_, i) => (
            <Box key={i} sx={theme => ({flex: '0 0 auto', width: {xs: 300, md: `calc((100% - ${theme.spacing(8)}) / 3)`}})}>
              <CardSkeleton />
            </Box>
          ))}
        </Box>
      </Container>
      <Box sx={{display: 'flex', justifyContent: 'center', mt: 4}}>
        <Skeleton variant="rounded" animation="wave" width={150} height={48} sx={theme => ({borderRadius: `${theme.radii.button}px`})} />
      </Box>
    </Section>
  );
}

export interface HomePageSkeletonProps {
  heroMode?: HeroMode;
  heroSize?: HeroSize;
}

// Mode and size mirror what the page passes its hero, so the placeholder is the
// height the hero will paint at and does not jump when the data lands.
export function HomePageSkeleton({heroMode = DEFAULT_HERO_MODE, heroSize = DEFAULT_HERO_SIZE}: HomePageSkeletonProps) {
  return (
    <>
      <MastheadSkeleton sizing={theme => heroFlowHeight(theme, heroMode, heroSize)} headerOverlay={heroMode === HeroMode.PinnedCurtain} />
      <SpotlightSkeleton />
      <CarouselSectionSkeleton />
    </>
  );
}

HomePageSkeleton.displayName = 'HomePageSkeleton';
CarouselSectionSkeleton.displayName = 'CarouselSectionSkeleton';
